import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  AssetInternalCommentsControllerGetAssetCommentsParams,
  GetAssetVersionCommentsSuccessDto,
  PasteAssetVersionCommentsDto
} from '@api/Api';
import {
  assetInternalCommentsGetAssetComments,
  assetInternalCommentsPasteAssetComments
} from '@api/Asset';
import { getPlaceholderQuery } from '@hooks/queries/helpers';

export const QUERY_COMMENTS_ASSETS = 'comments_assets';

export function useInternalCommentsAssetReviewQuery(
  params: AssetInternalCommentsControllerGetAssetCommentsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: GetAssetVersionCommentsSuccessDto;
  }
) {
  const queryKey = [QUERY_COMMENTS_ASSETS, params.assetVersionId];
  return useQuery({
    ...(options ?? {}),
    queryKey,
    queryFn: async ({ signal }) => {
      const { data } = await assetInternalCommentsGetAssetComments(params, {
        signal
      });
      return data;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      return getPlaceholderQuery<GetAssetVersionCommentsSuccessDto>(
        queryKey,
        (data): data is GetAssetVersionCommentsSuccessDto =>
          !!data &&
          typeof data === 'object' &&
          Array.isArray((data as GetAssetVersionCommentsSuccessDto).comments)
      );
    }
  });
}

export function usePasteAssetCommentsMutation({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
} = {}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: PasteAssetVersionCommentsDto) => {
      return await assetInternalCommentsPasteAssetComments(data);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_COMMENTS_ASSETS, variables.assetVersionId]
      });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });
  return mutation;
}
